import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import type { CalendarItemBundleData } from '../hooks/types'
import { getCalendarBundleDisplayPrice, getCalendarBundleWasPrice } from '../utils/bundlePricingDisplay'
import { getFnfInventoryMessage, getInventoryMessage, getInventoryMessageType, getPromotionInventoryMessage } from '../hooks/utils'

function useCalendarItemPricing({
  bundleData,
  luxPlusEnabled,
  priceMode,
}: {
  bundleData: CalendarItemBundleData
  luxPlusEnabled: boolean
  priceMode: 'total' | 'perPerson'
}) {
  const intl = useIntl()

  return useMemo(() => {
    const regularPrice = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
    const memberPrice = priceMode === 'perPerson' ? bundleData.memberPricePerPerson : bundleData.memberTotalPrice
    const isMemberPrice = luxPlusEnabled && !!memberPrice
    const displayPrice = getCalendarBundleDisplayPrice(bundleData, { useMemberPrice: luxPlusEnabled, priceMode })
    const wasPrice = getCalendarBundleWasPrice(bundleData, { useMemberPrice: luxPlusEnabled, priceMode })
    const shouldShowWasPrice = !!(bundleData.isFnfPriceApplied && wasPrice && displayPrice !== undefined && wasPrice > displayPrice)
    const inventoryMessageType = getInventoryMessageType(bundleData.seatsLeft, bundleData.roomsLeft)
    const regularInventoryMessage = getInventoryMessage(intl, bundleData.seatsLeft, bundleData.roomsLeft)
    const promotionInventoryMessage = getPromotionInventoryMessage(intl, bundleData.promotionSeatsLeft)
    const isPromotionPriceApplied = !!bundleData.isPromotionPriceApplied
    const isSoldOutInventoryMessage = inventoryMessageType === 'bundle-sold-out' || inventoryMessageType === 'hotel-sold-out'
    const inventoryMessage = !isSoldOutInventoryMessage && isPromotionPriceApplied && promotionInventoryMessage !== '' ?
      promotionInventoryMessage :
      regularInventoryMessage

    return {
      regularPrice,
      memberPrice,
      isMemberPrice,
      displayPrice,
      wasPrice,
      shouldShowWasPrice,
      inventoryMessageType,
      inventoryMessage,
      isPromotionPriceApplied,
      fnfInventoryMessage: getFnfInventoryMessage(intl, bundleData.fnfSeatsLeft),
      specialOfferLabel: intl.formatMessage({
        id: '15vWM49_cySUYJZ1ydW5',
        defaultMessage: 'Friends & Family Offer',
        description: 'Highlight label shown when Maldives calendar pricing includes FnF pricing',
      }),
    }
  }, [bundleData, intl, luxPlusEnabled, priceMode])
}

export default useCalendarItemPricing
